import { trackEvent, trackMetric } from "@entrilia/shared/logging";
import type { AuthChallengeOutcome } from "../../api/types/accessCheckApiTypes";
import { accessCheckEvents, accessCheckMetrics } from "../../trackingEventNames";
import { resolveClientCode } from "./accessCheckClientCode";
import type { BucketClassification } from "./types";

interface StartedProps {
  passed: boolean;
  failedCount: number;
  failedIds: string;
  email?: string;
}

export const emitAccessCheckStarted = ({ passed, failedCount, failedIds, email }: StartedProps): void => {
  trackEvent(accessCheckEvents.STARTED, {
    passed,
    failedCount,
    failedIds,
    ...(email ? { email } : {}),
    clientCode: resolveClientCode() ?? "",
  });
};

interface CodeRequestedProps {
  phase: "initial" | "resend";
  success: boolean;
  email: string;
  errorMessage?: string;
}

export const emitAccessCheckCodeRequested = ({ phase, success, email, errorMessage }: CodeRequestedProps): void => {
  trackEvent(accessCheckEvents.CODE_REQUESTED, {
    phase,
    success,
    email,
    ...(errorMessage !== undefined ? { errorMessage } : {}),
    clientCode: resolveClientCode() ?? "",
  });
};

interface CodeEnteredProps {
  outcome: AuthChallengeOutcome | "Error";
  email: string;
  hasInvestorPortalAccess?: boolean;
}

export const emitAccessCheckCodeEntered = ({ outcome, email, hasInvestorPortalAccess }: CodeEnteredProps): void => {
  trackEvent(accessCheckEvents.CODE_ENTERED, {
    outcome,
    email,
    ...(hasInvestorPortalAccess !== undefined ? { hasInvestorPortalAccess } : {}),
    clientCode: resolveClientCode() ?? "",
  });
};

interface ClassificationPropertyInputs {
  classification: BucketClassification;
  email: string | undefined;
  hasInvestorPortalAccess: boolean | undefined;
  clientCode: string;
}

const buildClassificationProperties = ({
  classification,
  email,
  hasInvestorPortalAccess,
  clientCode,
}: ClassificationPropertyInputs) => ({
  bucket: classification.bucket,
  title: classification.title,
  blockedFriendly: classification.blockedFriendly.join(","),
  blockedHosts: classification.blockedHosts.join(","),
  ...(email ? { email } : {}),
  ...(hasInvestorPortalAccess !== undefined ? { hasInvestorPortalAccess } : {}),
  clientCode,
});

interface CompletedProps {
  classification: BucketClassification;
  email?: string;
  hasInvestorPortalAccess?: boolean;
}

export const emitAccessCheckCompleted = ({ classification, email, hasInvestorPortalAccess }: CompletedProps): void => {
  const clientCode = resolveClientCode() ?? "";
  trackEvent(
    accessCheckEvents.COMPLETED,
    buildClassificationProperties({ classification, email, hasInvestorPortalAccess, clientCode })
  );
  const metricName = classification.bucket === 1 ? accessCheckMetrics.SUCCESS : accessCheckMetrics.ISSUE_DETECTED;
  trackMetric(metricName, 1, { bucket: classification.bucket, clientCode });
};

interface ReportDownloadedProps {
  classification: BucketClassification;
  email?: string;
  hasInvestorPortalAccess?: boolean;
}

export const emitAccessCheckReportDownloaded = ({
  classification,
  email,
  hasInvestorPortalAccess,
}: ReportDownloadedProps): void => {
  const clientCode = resolveClientCode() ?? "";
  trackEvent(
    accessCheckEvents.REPORT_DOWNLOADED,
    buildClassificationProperties({ classification, email, hasInvestorPortalAccess, clientCode })
  );
  trackMetric(accessCheckMetrics.REPORT_DOWNLOADED, 1, { bucket: classification.bucket, clientCode });
};
